// Offline-safe API wrapper for the Referee Scorekeeper.
//
// Behaviour:
// - When online → hit the API directly, cache match state locally.
// - When offline → replay actions locally against cached match state, queue
//   the action, and flush the queue automatically as soon as the network
//   returns (window "online" event + a 15 s poll).
// - Uses localStorage for simplicity — a match sheet must be OPENED at least
//   once while online so its data is cached on the device.
//
// Storage keys:
//   ref.match.<mid>  → last-known server state of the match (JSON)
//   ref.queue        → JSON array of pending {mid, path, body, at}
//   ref.online       → last online status (true/false) — read by UI

import api from "@/lib/api";

const MATCH_KEY = (mid) => `ref.match.${mid}`;
const QUEUE_KEY = "ref.queue";

function readJSON(key, fallback) { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } }
function writeJSON(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }

export function getCachedMatch(mid) { return readJSON(MATCH_KEY(mid), null); }
export function saveCachedMatch(match) { if (match?.id) writeJSON(MATCH_KEY(match.id), match); }

export function pendingCount() { return readJSON(QUEUE_KEY, []).length; }
export function isOnline() { return typeof navigator !== "undefined" ? navigator.onLine : true; }

function pushToQueue(item) {
  const q = readJSON(QUEUE_KEY, []);
  q.push({ ...item, at: Date.now() });
  writeJSON(QUEUE_KEY, q);
}

// ---- Local replay of point/undo/walkover so the UI updates offline too ----
function applyPointLocally(m, side) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.live = cloned.live || {};
  cloned.games = cloned.games?.length ? cloned.games : [[0, 0]];
  const cur = cloned.live.current_game ?? 0;
  while (cloned.games.length <= cur) cloned.games.push([0, 0]);
  const server = cloned.live.serving_side;
  const gameType = cloned.game_type || "rally";
  cloned.live.history = (cloned.live.history || []).concat([{ games: JSON.parse(JSON.stringify(cloned.games)), serving_side: server, current_game: cur }]).slice(-80);
  if (gameType === "side_out" && side !== server) {
    cloned.live.serving_side = side;
  } else {
    cloned.games[cur][side === "a" ? 0 : 1] += 1;
    if (gameType === "rally") cloned.live.serving_side = side;
  }
  const need = cloned.points_per_game || 11;
  const wb = cloned.win_by || 2;
  const bo = cloned.best_of || 1;
  const [pa, pb] = cloned.games[cur];
  if ((pa >= need || pb >= need) && Math.abs(pa - pb) >= wb) {
    const aWon = cloned.games.filter((g) => g[0] > g[1]).length;
    const bWon = cloned.games.filter((g) => g[1] > g[0]).length;
    if (aWon >= Math.floor(bo / 2) + 1 || bWon >= Math.floor(bo / 2) + 1) {
      cloned.status = "completed";
      cloned.winner_side = aWon > bWon ? "a" : "b";
      cloned.completed_at = new Date().toISOString();
    } else {
      cloned.live.current_game = cur + 1;
      cloned.games.push([0, 0]);
      const init = cloned.live.initial_server || "a";
      cloned.live.serving_side = (cloned.live.current_game % 2 === 0) ? init : (init === "a" ? "b" : "a");
    }
  }
  cloned.status = cloned.status || "in_progress";
  if (!cloned.winner_side) cloned.status = "in_progress";
  return cloned;
}

function applyUndoLocally(m) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.live = cloned.live || {};
  const history = cloned.live.history || [];
  if (!history.length) throw new Error("Nothing to undo");
  const snap = history.pop();
  cloned.games = snap.games;
  cloned.live.serving_side = snap.serving_side;
  cloned.live.current_game = snap.current_game;
  cloned.live.history = history;
  cloned.status = "in_progress";
  delete cloned.winner_side;
  delete cloned.completed_at;
  return cloned;
}

function applyWalkoverLocally(m, winner_side) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.games = winner_side === "a" ? [[6, 0]] : [[0, 6]];
  cloned.status = "walkover";
  cloned.winner_side = winner_side;
  cloned.completed_at = new Date().toISOString();
  return cloned;
}

function applyInjuryLocally(m, winner_side) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.games = winner_side === "a" ? [[11, 0]] : [[0, 11]];
  cloned.status = "completed";
  cloned.winner_side = winner_side;
  cloned.completed_at = new Date().toISOString();
  cloned.completion_reason = "injury";
  return cloned;
}

function applyUndoCompletionLocally(m) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.status = "scheduled";
  cloned.games = [];
  cloned.live = {};
  delete cloned.winner_side;
  delete cloned.completed_at;
  delete cloned.started_at;
  delete cloned.completion_reason;
  return cloned;
}

function applyTimeoutLocally(m, side) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.live = cloned.live || {};
  const cur = String(cloned.live.current_game || 0);
  const timeouts = cloned.live.timeouts || {};
  const game = timeouts[cur] || { a: false, b: false };
  if (game[side]) throw new Error(`Side ${side.toUpperCase()} already used its timeout for this game`);
  game[side] = true;
  timeouts[cur] = game;
  cloned.live.timeouts = timeouts;
  return cloned;
}

function applyTossLocally(m, winner_side, choice) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.live = cloned.live || {};
  const serving = choice === "serve" ? winner_side : (winner_side === "a" ? "b" : "a");
  cloned.live.toss = { winner_side, choice, at: new Date().toISOString() };
  cloned.live.initial_server = serving;
  cloned.live.serving_side = serving;
  cloned.live.current_game = 0;
  cloned.live.history = [];
  cloned.games = cloned.games?.length ? cloned.games : [[0, 0]];
  return cloned;
}

function applyStartLocally(m) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.status = "in_progress";
  cloned.started_at = new Date().toISOString();
  return cloned;
}

function applyScoreLocally(m, games) {
  const cloned = JSON.parse(JSON.stringify(m || {}));
  cloned.games = games;
  const aWon = games.filter((g) => g[0] > g[1]).length;
  const bWon = games.filter((g) => g[1] > g[0]).length;
  if (aWon !== bWon) {
    cloned.status = "completed";
    cloned.winner_side = aWon > bWon ? "a" : "b";
    cloned.completed_at = new Date().toISOString();
  } else {
    cloned.status = "in_progress";
  }
  return cloned;
}

// ---- Public: run an action either via API (if online) or locally (offline) ----
export async function runAction({ mid, path, body, localApply }) {
  const cached = getCachedMatch(mid);
  if (isOnline()) {
    try {
      const r = await api.post(path, body);
      // Best-effort: refresh cached match; caller may pass back an updated match too
      return { ok: true, data: r.data, offline: false };
    } catch (e) {
      // Real API error while online → bubble up; don't queue
      throw e;
    }
  }
  // Offline path — replay locally on cached match, queue the write
  if (!cached) throw new Error("Match not cached — open the match once while online");
  const nextMatch = localApply ? localApply(cached) : cached;
  saveCachedMatch(nextMatch);
  pushToQueue({ mid, path, body });
  return { ok: true, data: null, offline: true, match: nextMatch };
}

// ---- Flushing ----
let flushing = false;
export async function flushQueue() {
  if (flushing || !isOnline()) return { flushed: 0 };
  flushing = true;
  let flushed = 0;
  try {
    while (true) {
      const q = readJSON(QUEUE_KEY, []);
      if (!q.length) break;
      const item = q[0];
      try {
        await api.post(item.path, item.body);
        q.shift();
        writeJSON(QUEUE_KEY, q);
        flushed += 1;
      } catch (e) {
        // If server rejects (e.g. 409 completed), drop the item so we don't
        // block forever on a bad action.
        const status = e.response?.status;
        if (status && status >= 400 && status < 500) {
          q.shift();
          writeJSON(QUEUE_KEY, q);
        } else {
          break; // network hiccup — retry on next flush
        }
      }
    }
  } finally { flushing = false; }
  return { flushed };
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flushQueue(); });
  setInterval(() => { if (isOnline()) flushQueue(); }, 15000);
}

// ---- Helpers exported for the UI ----
export const localApplyPoint = applyPointLocally;
export const localApplyUndo = applyUndoLocally;
export const localApplyUndoCompletion = applyUndoCompletionLocally;
export const localApplyWalkover = applyWalkoverLocally;
export const localApplyInjury = applyInjuryLocally;
export const localApplyTimeout = applyTimeoutLocally;
export const localApplyToss = applyTossLocally;
export const localApplyStart = applyStartLocally;
export const localApplyScore = applyScoreLocally;
